import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import {
  AutoAwesome,
  Archive,
  Refresh,
  Close,
  Edit,
  Check,
  Cancel,
  Visibility,
  VisibilityOff,
  Delete,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { safeFormatDate, formatDateRange } from '../utils/dateUtils';
import { enGB } from 'date-fns/locale';
import { fetchSummaries, deleteSummary, renameSummary } from '../store/slices/summariesSlice';
import { fetchProcesses, deleteProcess } from '../store/slices/processesSlice';
import {
  addUploadSuccessNotification,
  addUploadFailedNotification,
  addDateRangeSuccessNotification,
  addDateRangeFailedNotification
} from '../store/slices/notificationSlice';
import EnhancedMarkdownRenderer from '../components/EnhancedMarkdownRenderer';
import backendUrl from '../store/config';

interface Summary {
  id: string;
  title: string;
  content: string;
  created_at: string;
  status: string;
  visible_to_users: boolean;
  // Time range fields
  start_date?: string;
  end_date?: string;
  date_range_display?: string;
  date_range?: string; // Optional human-readable date range (e.g. "2025-01-01 to 2025-01-31")
}

const SummaryManager: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Redux state
  const { summaries, loading: summariesLoading } = useAppSelector((state: any) => state.summaries);
  const { processes, loading: processLoading } = useAppSelector((state: any) => state.processes);
  const { user } = useAppSelector(state => state.auth);
  const token = useAppSelector((state: any) => state.auth.token);
  
  // Local UI state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState<Summary | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSummaryIds, setSelectedSummaryIds] = useState<Set<string>>(new Set());
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [processDeleteDialogOpen, setProcessDeleteDialogOpen] = useState(false);
  const [summaryToDelete, setSummaryToDelete] = useState<Summary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [summaryToRename, setSummaryToRename] = useState<Summary | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [summarySource, setSummarySource] = useState('starch');

  // Debug useEffect to monitor generating state changes
  useEffect(() => {
    console.log('Generating state changed to:', generating);
  }, [generating]);

  const loading = summariesLoading || processLoading || refreshLoading;

  useEffect(() => {
    dispatch(fetchSummaries());
    dispatch(fetchProcesses());
  }, [dispatch]);

  // Handle URL parameter for selected summary
  useEffect(() => {
    const selectedId = searchParams.get('selected');
    if (selectedId && summaries.length > 0) {
      const summary = summaries.find((s: Summary) => s.id === selectedId);
      if (summary) {
        setSelectedSummary(summary);
        // Clear the URL parameter after selection
        setSearchParams({});
      } else {
        // Not in the loaded list (it is capped) - open the summary on its own page
        navigate(`/summaries/${selectedId}`, { replace: true });
      }
    }
  }, [summaries, searchParams, setSearchParams, navigate]);

  // Handle checkbox selection
  const handleSummarySelect = (summaryId: string, checked: boolean) => {
    const newSelected = new Set(selectedSummaryIds);
    if (checked) {
      newSelected.add(summaryId);
    } else {
      newSelected.delete(summaryId);
    }
    setSelectedSummaryIds(newSelected);
  };

  // Handle select all/none
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set<string>(completedSummaries.map((s: Summary) => s.id));
      setSelectedSummaryIds(allIds);
    } else {
      setSelectedSummaryIds(new Set<string>());
    }
  };

  // Update visibility for all summaries based on selection
  const updateSummariesVisibility = async () => {
    setUpdatingVisibility(true);
    try {
      // Get all summary IDs
      const allSummaryIds = completedSummaries.map((s: Summary) => s.id);
      
      // Make selected summaries visible
      if (selectedSummaryIds.size > 0) {
        const makeVisibleResponse = await fetch(`${backendUrl}/api/summaries/visibility`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            summary_ids: Array.from(selectedSummaryIds),
            visible_to_users: true
          })
        });
        
        if (!makeVisibleResponse.ok) {
          throw new Error('Failed to make summaries visible');
        }
      }
      
      // Make unselected summaries hidden
       const unselectedIds = allSummaryIds.filter((id: string) => !selectedSummaryIds.has(id));
      if (unselectedIds.length > 0) {
        const makeHiddenResponse = await fetch(`${backendUrl}/api/summaries/visibility`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            summary_ids: unselectedIds,
            visible_to_users: false
          })
        });
        
        if (!makeHiddenResponse.ok) {
          throw new Error('Failed to hide summaries');
        }
      }
      
      setSnackbarMessage(`Visibility updated: ${selectedSummaryIds.size} visible, ${unselectedIds.length} hidden`);
      setSnackbarOpen(true);
      
      // Refresh summaries to get updated data
      dispatch(fetchSummaries());
      
      // Clear selection and exit edit mode
      setSelectedSummaryIds(new Set());
      setEditMode(false);
    } catch (error) {
      console.error('Error updating visibility:', error);
      setSnackbarMessage('Failed to update summary visibility');
      setSnackbarOpen(true);
    } finally {
      setUpdatingVisibility(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.zip')) {
        setSelectedFile(file);
        // Auto-upload the file
        uploadFile(file);
      } else {
        dispatch(addUploadFailedNotification({
          userId: user?.id || '',
          error: 'Please select a ZIP file containing email files.'
        }));
      }
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(event.dataTransfer.files);
    const zipFile = files.find(file => file.name.endsWith('.zip'));
    
    if (zipFile) {
      setSelectedFile(zipFile);
      // Auto-upload the file
      uploadFile(zipFile);
    } else {
      dispatch(addUploadFailedNotification({
        userId: user?.id || '',
        error: 'Please drop a ZIP file containing email files.'
      }));
    }
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source', summarySource);

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${backendUrl}/api/upload?source=${encodeURIComponent(summarySource)}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Show immediate upload success notification
        dispatch(addUploadSuccessNotification({
          userId: user?.id || '',
          filename: file.name,
          processId: result.job_id
        }));
        
        // Track success notification time for delay logic
        const successTime = Date.now();
        if (window.notificationProvider) {
          window.notificationProvider.lastSuccessNotificationTime = successTime;
        }
        
        // Show delayed "Summary Generation Started" notification after 2 seconds
        setTimeout(() => {
          // This will be handled by WebSocket, but we ensure 2-second gap
        }, 2000);
        
        // Check process status after a short delay to detect failures
        setTimeout(async () => {
          try {
            const statusResponse = await fetch(`${backendUrl}/api/processes/${result.job_id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (statusResponse.ok) {
              const processData = await statusResponse.json();
              if (processData.status === 'failed') {
                // Show failure notification and hide success
                dispatch(addUploadFailedNotification({
                  userId: user?.id || '',
                  error: processData.error_message || 'Upload processing failed. Please try again.',
                  processId: result.job_id
                }));
              }
            }
          } catch (error) {
            console.error('Error checking process status:', error);
          }
        }, 5000); // Check after 5 seconds
        
        setSelectedFile(null);
        dispatch(fetchSummaries());
        dispatch(fetchProcesses());
      } else {
        const error = await response.json();
        let errorMessage = 'Failed to upload file.';
        if (error.detail) {
          if (error.detail.includes('empty') || error.detail.includes('.eml')) {
            errorMessage = 'ZIP file is empty or does not contain valid .eml files.';
          } else {
            errorMessage = error.detail;
          }
        }
        
        // Show error notification
        dispatch(addUploadFailedNotification({
          userId: user?.id || '',
          error: errorMessage
        }));
      }
    } catch (error) {
      // Show error notification
      dispatch(addUploadFailedNotification({
        userId: user?.id || '',
        error: 'Network error occurred while uploading.'
      }));
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateFromDateRange = async () => {
    if (!startDate || !endDate) {
      dispatch(addDateRangeFailedNotification({
        userId: user?.id || '',
        error: 'Please select both start and end dates.'
      }));
      return;
    }

    if (endDate < startDate) {
      dispatch(addDateRangeFailedNotification({
        userId: user?.id || '',
        error: 'End date must be after start date.'
      }));
      return;
    }

    console.log('Starting date range export, setting generating to true');
    setGenerating(true);
    
    // Provide immediate feedback after a short delay to simulate request acceptance
    setTimeout(() => {
      console.log('Providing immediate feedback, stopping loading state');
      setGenerating(false);
      console.log('setGenerating(false) called after immediate feedback');
      
      // Show immediate success notification
      console.log('Dispatching success notification for request acceptance');
      dispatch(addDateRangeSuccessNotification({
        userId: user?.id || '',
        dateRange: `${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`
      }));
      
      // Track success notification time for delay logic
      const successTime = Date.now();
      if (window.notificationProvider) {
        window.notificationProvider.lastSuccessNotificationTime = successTime;
      }
    }, 500); // 500ms delay to simulate request processing

    if (!token) {
      setGenerating(false);
      dispatch(addDateRangeFailedNotification({
        userId: user?.id || '',
        error: 'No authentication token found. Please login again.'
      }));
      return;
    }

    // Send the POST request in the background (don't await it)
    fetch(`${backendUrl}/api/export-by-date-range`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
        source: summarySource,
      })
    }).then(async (response) => {
      if (response.ok) {
        const result = await response.json();
        console.log('POST request completed successfully:', result);

        // Update the success notification with process ID if available
        if (result.job_id) {
          dispatch(addDateRangeSuccessNotification({
            userId: user?.id || '',
            dateRange: `${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`,
            processId: result.job_id
          }));
        }

        // Refresh data after POST completes
        dispatch(fetchSummaries());
        dispatch(fetchProcesses());
      } else {
        const error = await response.json();
        console.log('POST request failed:', error);

        // Handle structured error responses
        let errorMessage = 'Failed to generate summary from date range.';
        let detailedError = '';

        if (error.detail) {
          if (typeof error.detail === 'object' && error.detail.message) {
            // Handle structured error response
            errorMessage = error.detail.message;
            if (error.detail.details) {
              detailedError = error.detail.details;
            }

            // Add suggested actions if available
            if (error.detail.suggested_actions && error.detail.suggested_actions.length > 0) {
              detailedError += '\n\nSuggested actions:\n' +
                error.detail.suggested_actions.map((action: string) => `• ${action}`).join('\n');
            }

            // Special handling for authentication errors
            if (error.detail.error_type === 'authentication_disabled') {
              errorMessage = 'Email Service Account Disabled';
              detailedError = 'The email service account (S_AI_NewNow@agrana.net) is disabled. ' +
                'Please contact your IT administrator to resolve this issue.\n\n' + detailedError;
            } else if (error.detail.error_type === 'authentication_error') {
              errorMessage = 'Email Service Authentication Failed';
            }
          } else if (Array.isArray(error.detail)) {
            // Handle FastAPI validation errors
            errorMessage = error.detail.map((err: any) => `${err.loc?.join('.')} - ${err.msg}`).join(', ');
          } else if (typeof error.detail === 'string') {
            errorMessage = error.detail;
          }
        }

        // Show error notification for POST failure
        dispatch(addDateRangeFailedNotification({
          userId: user?.id || '',
          error: detailedError ? `${errorMessage}\n\n${detailedError}` : errorMessage
        }));
      }
    }).catch((error) => {
      console.log('POST request network error:', error);
      dispatch(addDateRangeFailedNotification({
        userId: user?.id || '',
        error: 'Network error occurred while generating summary.'
      }));
    });
  };

  const handleStartDateChange = (newStartDate: Date | null) => {
    if (endDate && newStartDate && endDate < newStartDate) {
      setEndDate(null);
    }
    setStartDate(newStartDate);
  };

  const handleEndDateChange = (newEndDate: Date | null) => {
    if (startDate && newEndDate && newEndDate < startDate) {
      dispatch(addDateRangeFailedNotification({
        userId: user?.id || '',
        error: 'End date must be after start date.'
      }));
      return;
    }
    setEndDate(newEndDate);
  };

  // Handle delete summary click
  const handleDeleteClick = (summary: Summary) => {
    setSummaryToDelete(summary);
    // Check if summary has an associated process
    const associatedProcess = processes.find((p: any) => 
      p.summaries && p.summaries.some((s: any) => s.id === summary.id)
    );
    
    if (associatedProcess) {
      // Show process deletion dialog directly
      setProcessDeleteDialogOpen(true);
    } else {
      // No associated process, show simple confirmation
      setDeleteDialogOpen(true);
    }
  };

  const handleRenameClick = (summary: Summary, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSummaryToRename(summary);
    setRenameTitle(summary.title || '');
    setRenameDialogOpen(true);
  };

  const handleRenameCancel = () => {
    setRenameDialogOpen(false);
    setSummaryToRename(null);
    setRenameTitle('');
  };

  const handleRenameConfirm = async () => {
    if (!summaryToRename) return;
    const nextTitle = renameTitle.trim();
    if (!nextTitle) {
      setSnackbarMessage('Title cannot be empty');
      setSnackbarOpen(true);
      return;
    }

    setRenaming(true);
    try {
      const updated = await dispatch(renameSummary({ id: summaryToRename.id, title: nextTitle })).unwrap();
      setSnackbarMessage('Summary renamed successfully');
      setSnackbarOpen(true);
      setRenameDialogOpen(false);
      setSummaryToRename(null);
      setRenameTitle('');

      // Keep local selectedSummary in sync
      setSelectedSummary((prev) => (prev?.id === updated.id ? { ...prev, title: updated.title } : prev));
    } catch (error) {
      console.error('Error renaming summary:', error);
      setSnackbarMessage(typeof error === 'string' ? error : 'Failed to rename summary');
      setSnackbarOpen(true);
    } finally {
      setRenaming(false);
    }
  };

  // Handle delete confirmation for simple summary deletion
  const handleDeleteConfirm = async () => {
    if (!summaryToDelete) return;
    await deleteSummaryOnly();
  };

  // Delete summary only
  const deleteSummaryOnly = async () => {
    if (!summaryToDelete) return;
    
    setDeleting(true);
    try {
      await dispatch(deleteSummary(summaryToDelete.id)).unwrap();
      setSnackbarMessage(`Summary "${summaryToDelete.title}" deleted successfully`);
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setProcessDeleteDialogOpen(false);
      setSummaryToDelete(null);
      // Refresh summaries
      dispatch(fetchSummaries());
    } catch (error) {
      console.error('Error deleting summary:', error);
      setSnackbarMessage('Failed to delete summary');
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  // Delete summary and associated process
  const deleteSummaryAndProcess = async () => {
    if (!summaryToDelete) return;
    
    const associatedProcess = processes.find((p: any) => 
      p.summaries && p.summaries.some((s: any) => s.id === summaryToDelete.id)
    );
    
    setDeleting(true);
    try {
      if (associatedProcess) {
        // Delete the process (which will also delete associated summaries)
        await dispatch(deleteProcess(associatedProcess.id)).unwrap();
        setSnackbarMessage(`Summary and associated process deleted successfully`);
      } else {
        // Fallback to summary deletion only
        await dispatch(deleteSummary(summaryToDelete.id)).unwrap();
        setSnackbarMessage(`Summary "${summaryToDelete.title}" deleted successfully`);
      }
      setSnackbarOpen(true);
      setProcessDeleteDialogOpen(false);
      setSummaryToDelete(null);
      // Refresh both summaries and processes
      dispatch(fetchSummaries());
      dispatch(fetchProcesses());
    } catch (error) {
      console.error('Error deleting summary and process:', error);
      setSnackbarMessage('Failed to delete summary and process');
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSummaryToDelete(null);
  };

  // Handle process delete cancellation
  const handleProcessDeleteCancel = () => {
    setProcessDeleteDialogOpen(false);
    setSummaryToDelete(null);
  };

  const completedSummaries = summaries
    .filter((summary: Summary) => summary.status === 'completed')
    .sort((a: Summary, b: Summary) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return (
    <Box 
      sx={{ 
        display: 'flex',
        flexGrow: 1,
        height: '100%', 
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Fade in timeout={600}>
        <Box sx={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row'
        }}>          
          {/* Summary Selection Sidebar */}
          <Box
            sx={{
              width: isMobile ? '100%' : (isTablet ? 280 : 300),
              minWidth: isMobile ? 'auto' : (isTablet ? 270 : 290),
              height: isMobile ? 'auto' : '100%',
              maxHeight: isMobile ? '50vh' : '100%',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1050,
              borderRight: isMobile ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
              borderBottom: isMobile ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
            }}
          >
            {/* Scrollable Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: isMobile ? 1.5 : 2,
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? 1.5 : 2,
                scrollbarGutter: 'stable',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'rgba(0,0,0,0.3)',
                },
              }}
            >
              {/* Source Selector */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                  DATA SOURCE
                </Typography>
                <ToggleButtonGroup
                  value={summarySource}
                  exclusive
                  onChange={(_, newSource) => {
                    if (newSource !== null) setSummarySource(newSource);
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      py: 0.5,
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        backgroundColor: '#a31830',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#8a1428' },
                      },
                    },
                  }}
                >
                  <ToggleButton value="starch">Starch</ToggleButton>
                  <ToggleButton value="non_food">Non-Food</ToggleButton>
                  <ToggleButton value="sns">SNS</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* ZIP Upload Section */}
              <Box>
                <Box
                    sx={{
                      border: (theme) => `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
                      borderRadius: isMobile ? 1 : 2,
                      p: isMobile ? 2 : 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: (theme) => isDragOver ? 
                        (theme.palette.mode === 'dark' ? 'rgba(163, 24, 48, 0.1)' : '#fef5f5') : 
                        (theme.palette.mode === 'dark' ? '#2a2a2a' : '#f7fafc'),
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#333' : '#edf2f7'
                      }
                    }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('zip-upload-input')?.click()}
                >
                  <Archive sx={{ fontSize: isMobile ? 32 : 40, color: 'text.secondary', mb: 1 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary', 
                      mb: 1,
                      fontSize: isMobile ? '0.8rem' : '0.875rem'
                    }}
                  >
                    {selectedFile ? selectedFile.name : 'Drop ZIP file here or click to browse'}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.disabled',
                      fontSize: isMobile ? '0.7rem' : '0.75rem'
                    }}
                  >
                    ZIP files containing .eml email files
                  </Typography>
                  <input
                    id="zip-upload-input"
                    type="file"
                    accept=".zip"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                </Box>
                {/* Loading indicator */}
              {uploading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                    Uploading...
                  </Typography>
                </Box>
              )}
              </Box>

              <Divider />

              {/* Date Range Section */}
              <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: isMobile ? 1.5 : 2, fontSize: isMobile ? "0.7rem" : "0.9rem" }}>
                  Generate from Date Range
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      format="MMM dd, yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: {
                            '& .MuiInputBase-input': {
                              fontSize: isMobile ? '0.875rem' : '0.75rem'
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: isMobile ? '0.875rem' : '0.75rem'
                            }
                          }
                        },
                        popper: {
                          sx: {
                            '& .MuiPaper-root': {
                              maxWidth: isMobile ? '90vw' : 'auto',
                              margin: isMobile ? '0 auto' : 'auto',
                            },
                            '& .MuiPickersCalendarHeader-root': {
                              paddingLeft: isMobile ? 1 : 2,
                              paddingRight: isMobile ? 1 : 2,
                            },
                            '& .MuiDayCalendar-root': {
                              minHeight: isMobile ? '200px' : 'auto',
                            },
                            '& .MuiPickersDay-root': {
                              fontSize: isMobile ? '0.875rem' : '0.75rem',
                              width: isMobile ? '32px' : '36px',
                              height: isMobile ? '32px' : '36px',
                            }
                          }
                        }
                      }}
                    />
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      minDate={startDate || undefined}
                      format="MMM dd, yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: {
                            '& .MuiInputBase-input': {
                              fontSize: isMobile ? '0.875rem' : '0.75rem'
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: isMobile ? '0.875rem' : '0.75rem'
                            }
                          }
                        },
                        popper: {
                          sx: {
                            '& .MuiPaper-root': {
                              maxWidth: isMobile ? '90vw' : 'auto',
                              margin: isMobile ? '0 auto' : 'auto',
                            },
                            '& .MuiPickersCalendarHeader-root': {
                              paddingLeft: isMobile ? 1 : 2,
                              paddingRight: isMobile ? 1 : 2,
                            },
                            '& .MuiDayCalendar-root': {
                              minHeight: isMobile ? '200px' : 'auto',
                            },
                            '& .MuiPickersDay-root': {
                              fontSize: isMobile ? '0.875rem' : '0.75rem',
                              width: isMobile ? '32px' : '36px',
                              height: isMobile ? '32px' : '36px',
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                </LocalizationProvider>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    console.log('Button clicked, current generating state:', generating);
                    console.log('Button disabled conditions - startDate:', !!startDate, 'endDate:', !!endDate, 'generating:', generating);
                    console.log('Button disabled result:', !startDate || !endDate || generating);
                    handleGenerateFromDateRange();
                  }}
                  disabled={!startDate || !endDate || generating}
                  sx={{ mt: 2 }}
                >
                  {generating ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      Generating...
                    </>
                  ) : (
                    <>
                      <AutoAwesome sx={{ mr: 1 }} />
                      Generate Summary
                    </>
                  )}
                </Button>
              </Box>

              <Divider />

              {/* Generated Summaries List */}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isMobile ? "center" : "space-between",
                    position: "relative",
                    mb: 2,
                  }}
                >
                  {/* Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textAlign: isMobile ? "center" : "left",
                      flex: isMobile ? 1 : "unset",
                      fontSize: isMobile ? "0.7rem" : "0.9rem",
                    }}
                  >
                    Generated Summaries ({completedSummaries.length})
                  </Typography>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) && (
                      <Tooltip title={editMode ? "Cancel edit" : "Edit summaries"}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (editMode) {
                              setEditMode(false);
                              setSelectedSummaryIds(new Set());
                            } else {
                              setEditMode(true);
                              // Pre-select summaries that are currently visible to users
                              const visibleSummaryIds = completedSummaries
                                .filter((summary: { visible_to_users: any; }) => summary.visible_to_users)
                                .map((summary: { id: any; }) => summary.id);
                              setSelectedSummaryIds(new Set(visibleSummaryIds));
                            }
                          }}
                          sx={{ p: 0.5 }}
                        >
                          {editMode ? <Cancel sx={{ fontSize: isMobile ? 18 : 20 }} /> : <Edit sx={{ fontSize: isMobile ? 18 : 20 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {/* Refresh button */}
                    <Tooltip title="Refresh">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          setRefreshLoading(true);
                          const startTime = Date.now();
                          try {
                            await Promise.all([dispatch(fetchSummaries()), dispatch(fetchProcesses())]);
                          } catch (error) {
                            console.error("Error refreshing data:", error);
                          }
                          const elapsedTime = Date.now() - startTime;
                          const remainingTime = Math.max(0, 1500 - elapsedTime);
                          if (remainingTime > 0) {
                            await new Promise((resolve) => setTimeout(resolve, remainingTime));
                          }
                          setRefreshLoading(false);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <Refresh sx={{ fontSize: isMobile ? 22 : 24 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Edit mode controls */}
                {editMode && user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSummaryIds.size === completedSummaries.length && completedSummaries.length > 0}
                            indeterminate={selectedSummaryIds.size > 0 && selectedSummaryIds.size < completedSummaries.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Select All"
                      />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {selectedSummaryIds.size} selected
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Check />}
                        onClick={updateSummariesVisibility}
                        disabled={updatingVisibility}
                        sx={{ minWidth: 'auto' }}
                      >
                        Update Visibility
                      </Button>
                    </Box>
                  </Box>
                )}
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Loading summaries...
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {completedSummaries.map((summary: Summary) => (
                    <Card
                      key={summary.id}
                      sx={{
                        cursor: editMode ? 'default' : 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: editMode ? 'none' : 'translateY(-2px)',
                          boxShadow: (theme) => editMode ? theme.shadows[1] : theme.shadows[4]
                        },
                        border: '2px solid',
                        borderColor: (editMode && selectedSummaryIds.has(summary.id)) || (!editMode && selectedSummary?.id === summary.id) ? 'primary.main' : 'transparent'
                      }}
                      onClick={() => !editMode && setSelectedSummary(summary)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, textAlign: isMobile ? 'center' : 'left', position: 'relative' }}>
                        {/* Icons positioned in top right corner */}
                        {user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) && (
                          <Box sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            zIndex: 1
                          }}>
                            {!editMode && (
                              <Tooltip title="Rename summary">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleRenameClick(summary, e)}
                                  sx={{ p: 0.25 }}
                                  aria-label="Rename summary"
                                >
                                  <Edit sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {editMode && (
                              <Tooltip title="Delete summary">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(summary)}
                                  sx={{ 
                                    p: 0.25,
                                    '&:hover': {
                                      color: 'error.contrastText'
                                    }
                                  }}
                                >
                                  <Delete sx={{ fontSize: 16, color: 'error.main' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={summary.visible_to_users ? 'Visible to users' : 'Hidden from users'}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {summary.visible_to_users ? (
                                  <Visibility sx={{ fontSize: 16, color: 'success.main' }} />
                                ) : (
                                  <VisibilityOff sx={{ fontSize: 16, color: 'text.disabled' }} />
                                )}
                              </Box>
                            </Tooltip>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          {editMode && user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) && (
                            <Checkbox
                              checked={selectedSummaryIds.has(summary.id)}
                              onChange={(e) => handleSummarySelect(summary.id, e.target.checked)}
                              size="small"
                              sx={{ mt: -0.5 }}
                            />
                          )}
                          <Box sx={{ flex: 1, pr: user?.role && ['admin', 'anis-admin', 'superadmin'].includes(user.role) ? 5 : 0 }}>
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                {summary.title.replace(/\s*[-–—]\s*\d{2}[-./]\d{2}[-./]\d{4}\s*[-–—]\s*\d{2}[-./]\d{2}[-./]\d{4}\s*$/, '')}
                              </Typography>
                              {summary.date_range_display && (
                                <Chip
                                  label={summary.date_range_display}
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    backgroundColor: 'grey.200',
                                    color: 'text.secondary',
                                    fontSize: '0.7rem',
                                    height: 22,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                              {summary.content.substring(0, 80)}...
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                              {safeFormatDate(summary.created_at, 'MMM dd, yyyy HH:mm')}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  {completedSummaries.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No summaries generated yet
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        Upload a ZIP file or select a date range to get started
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex',
            flexDirection: 'column',
            height: isMobile ? 'auto' : '100%',
            minHeight: isMobile ? '50vh' : 'auto',
            overflow: 'hidden'
          }}>
            {/* Main Content */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex',
              flexDirection: 'column',
              p: isMobile ? 2 : 4,
              overflow: 'hidden'
            }}>
              {!selectedSummary ? (
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  textAlign: 'center', 
                  maxWidth: isMobile ? '100%' : 600, 
                  mx: 'auto',
                  px: isMobile ? 2 : 0 
                }}>
                  <Box>
                    <AutoAwesome sx={{ fontSize: isMobile ? 60 : 80, color: 'primary.main', mb: 2 }} />
                    <Typography 
                      variant={isMobile ? "h5" : "h4"} 
                      sx={{ 
                        mb: 2, 
                        fontWeight: 600,
                        fontSize: isMobile ? '1.5rem' : '2.125rem'
                      }}
                    >
                      Email Summary Generator
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.secondary', 
                        mb: 4, 
                        lineHeight: 1.6,
                        fontSize: isMobile ? '0.9rem' : '1rem'
                      }}
                    >
                      {completedSummaries.length > 0 
                        ? 'Select a summary to display here' 
                        : 'Generate a summary by uploading a zip or selecting date range'
                      }
                    </Typography>


                  </Box>
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'auto'
                }}>
                  {/* Summary Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    mb: 3,
                    pb: 2,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                  }}>
                    <Box sx={{ flex: 1, mr: 2 }}>
                      <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 600, mb: 1 }}>
                        {selectedSummary.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {safeFormatDate(selectedSummary.created_at, 'MMM dd, yyyy HH:mm')}
                        </Typography>
                        {selectedSummary.date_range && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Date range: {formatDateRange(selectedSummary.date_range)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Button
                      onClick={() => setSelectedSummary(null)}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      <Close />
                    </Button>
                  </Box>
                  
                  {/* Summary Content */}
                  <EnhancedMarkdownRenderer 
                    content={selectedSummary.content}
                    variant="full"
                  />
                </Box>
              )}
            </Box>
          </Box>




        </Box>
      </Fade>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Summary
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the summary "{summaryToDelete?.title}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
            autoFocus
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Process Delete Confirmation Dialog */}
      <Dialog
        open={processDeleteDialogOpen}
        onClose={handleProcessDeleteCancel}
        aria-labelledby="process-delete-dialog-title"
        aria-describedby="process-delete-dialog-description"
      >
        <DialogTitle id="process-delete-dialog-title">
          Delete Associated Process?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="process-delete-dialog-description">
            This summary is associated with a processing job. Would you like to:
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              • <strong>Delete summary only:</strong> Keep the processing job but remove this summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              • <strong>Delete both:</strong> Remove the summary and its associated processing job
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProcessDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={deleteSummaryOnly} 
            color="warning"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete Summary Only'}
          </Button>
          <Button 
            onClick={deleteSummaryAndProcess} 
            color="error" 
            variant="contained"
            disabled={deleting}
            autoFocus
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete Both'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Summary Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={handleRenameCancel}
        aria-labelledby="rename-summary-dialog-title"
      >
        <DialogTitle id="rename-summary-dialog-title">Rename Summary</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a new title for "{summaryToRename?.title}".
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Title"
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            disabled={renaming}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameCancel} disabled={renaming}>
            Cancel
          </Button>
          <Button onClick={handleRenameConfirm} variant="contained" disabled={renaming}>
            {renaming ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarMessage.includes('Failed') ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SummaryManager;
